import { ApolloClient, ApolloLink, from, HttpLink, NormalizedCacheObject } from '@apollo/client';
import { CONSTANTS } from 'config/constants';
import { cache } from './cache';
import { onError } from '@apollo/client/link/error';
import { GraphQLError } from 'graphql';
import { RetryLink } from '@apollo/client/link/retry';
import { isSignedInVar } from 'apollo/reactive-vars';
import { reportApolloNetworkError } from 'error-reporting/error-reporting';

const { API_URI } = CONSTANTS;

// The session is an HttpOnly cookie set by super-admin-backend on its own host;
// the browser sends it, this code never sees it.
const http = new HttpLink({
    uri: API_URI,
    credentials: 'include',
});

const middleware = new ApolloLink((operation, forward) => {
    if (operation.variables) {
        const omitTypename = (key: string, value: unknown) => {
            return key === '__typename' ? undefined : value;
        };
        operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
    }
    operation.setContext({
        headers: {
            // Forces a CORS preflight, so the backend's origin check runs before
            // the session cookie is used (also required by the backend).
            'apollo-require-preflight': '1',
        },
    });
    return forward(operation);
});

// Report all GQl error, in case we forgot to use showBoundary()
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors)
        graphQLErrors.forEach((e: GraphQLError) => {
            // Session expired or ended elsewhere: show the login page again.
            if (e.extensions?.code === 'UNAUTHENTICATED') {
                isSignedInVar(false);
            }
        });
    if (networkError) {
        console.error(networkError);
        // A non-2xx response with GraphQL errors in its body arrives as both; the backend logs those.
        if (!graphQLErrors?.length) {
            reportApolloNetworkError(networkError, operation?.operationName);
        }
    }
});

// classify network errors
const classifyNetworkError = (err: any): 'aborted' | 'network' | 'other' => {
    if (!err) return 'other';
    if (err.name === 'AbortError') return 'aborted';
    const msg = String(err.message || '');
    if (
        msg.includes('Failed to fetch') ||
        msg.includes('Load failed') ||
        msg.includes('NetworkError') ||
        msg.includes('TypeError')
    ) {
        return 'network';
    }
    return 'other';
};

// Default: retries queries/subscriptions on network erros; NOT mutations
const retryLink = new RetryLink({
    delay: {
        initial: 250,
        max: 4000,
        jitter: true,
    },
    attempts: (count, operation, error) => {
        console.log(`Retry attempt #${count} for ${operation.operationName}`);
        // Only for network-type failures (not aborts, not GraphQL errors)
        const kind = classifyNetworkError(error);
        if (kind !== 'network') return false;

        // Do not retry mutations unless explicitly allowed
        const isMutation = operation.query.definitions.some(
            (d: any) => d.kind === 'OperationDefinition' && d.operation === 'mutation',
        );
        if (isMutation) return false;

        return count <= 2;
    },
});

export const apolloClient = new ApolloClient<NormalizedCacheObject>({
    link: from([errorLink, retryLink, middleware, http]),
    cache,
    defaultContext: {
        includeExtensions: true,
        tracing: true,
    },
    defaultOptions: {
        query: {
            context: {
                includeExtensions: true,
                tracing: true,
            },
        },
    },
    // We can add client side graphql type definitions here using typeDefs property
});
